
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DollarSign, Heart, BookOpen, Smile, Briefcase } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { calculatorCategories } from '@/lib/data/calculatorCategories';

/** Icon mapping for each category theme */
const categoryIcons: Record<string, React.ReactNode> = {
  'financial-tools': <DollarSign className="h-4 w-4 text-finance" />,
  'health-wellness': <Heart className="h-4 w-4 text-health" />,
  'education': <BookOpen className="h-4 w-4 text-everyday" />,
  'lifestyle': <Smile className="h-4 w-4 text-[hsl(var(--lifestyle))]" />,
  'professional-tools': <Briefcase className="h-4 w-4 text-professional" />,
};

const Navigation: React.FC = () => {
  const location = useLocation();

  return (
    <NavigationMenu className="hidden md:flex">
      <NavigationMenuList>
        {calculatorCategories.map((category) => {
          const isActive = location.pathname.startsWith(`/calculators/${category.id}`);
          const icon = categoryIcons[category.id];

          return (
            <NavigationMenuItem key={category.id}>
              <NavigationMenuTrigger
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md",
                  isActive && "bg-accent text-accent-foreground"
                )}
              >
                {icon}
                <span>{category.name}</span>
              </NavigationMenuTrigger>

              {/* Dropdown preview showing calculators in this category */}
              <NavigationMenuContent>
                <div className="grid w-[400px] gap-1 p-3 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                  {category.calculators.slice(0, 8).map((calc) => (
                    <NavigationMenuLink key={calc.id} asChild>
                      <Link
                        to={`/calculators/${category.id}/${calc.id}`}
                        className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      >
                        <div className="text-sm font-medium leading-none">{calc.name}</div>
                        <p className="line-clamp-1 text-xs leading-snug text-muted-foreground mt-1">
                          {calc.description}
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  ))}
                  {category.calculators.length > 8 && (
                    <NavigationMenuLink asChild>
                      <Link
                        to={`/calculators/${category.id}`}
                        className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent text-primary text-sm font-medium"
                      >
                        View all {category.calculators.length} →
                      </Link>
                    </NavigationMenuLink>
                  )}
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default Navigation;
